import type {
  CollaborationInboxItem,
  CollaborationShareInvitation
} from './collaborationControlPlaneShared';
import type { CollaborationThreadArtifact } from './collaborationThreadShared';

export type CollaborationWorkDirection = 'sent' | 'received';
export type CollaborationSeenState = Record<string, string>;

export interface CollaborationEvidenceSummary {
  count: number;
  quarantinedCount: number;
  releasedCount: number;
  technicalFailureCount: number;
  label: string;
  tone: 'neutral' | 'warning' | 'success';
}

const normalizedIdentityPart = (value: string): string =>
  String(value || '').trim().toLowerCase();

export const collaborationSeenStorageKey = ({
  tenantId,
  userEmail
}: {
  tenantId: string;
  userEmail: string;
}): string =>
  `edinshall:collaboration:seen:v1:${encodeURIComponent(normalizedIdentityPart(tenantId))}:${encodeURIComponent(normalizedIdentityPart(userEmail))}`;

export const collaborationSeenThreadKey = (
  direction: CollaborationWorkDirection,
  threadId: string
): string => `${direction}:${String(threadId || '').trim()}`;

export const parseCollaborationSeenState = (value: string | null): CollaborationSeenState => {
  if (!value) return {};
  try {
    const parsed = JSON.parse(value) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>)
        .filter(([key, timestamp]) => key.trim() && typeof timestamp === 'string' && !Number.isNaN(Date.parse(timestamp)))
        .map(([key, timestamp]) => [key, String(timestamp)])
    );
  } catch {
    return {};
  }
};

export const markCollaborationThreadSeen = ({
  state,
  direction,
  threadId,
  activityAt
}: {
  state: CollaborationSeenState;
  direction: CollaborationWorkDirection;
  threadId: string;
  activityAt: string;
}): CollaborationSeenState => ({
  ...state,
  [collaborationSeenThreadKey(direction, threadId)]: activityAt
});

export const isCollaborationThreadNew = ({
  state,
  direction,
  threadId,
  activityAt,
  createdAt
}: {
  state: CollaborationSeenState;
  direction: CollaborationWorkDirection;
  threadId: string;
  activityAt: string;
  createdAt?: string;
}): boolean => {
  const activityMs = Date.parse(activityAt);
  if (!Number.isFinite(activityMs)) return false;

  // A newly-created sent item is the administrator's own activity. It only
  // becomes new when the record changes after creation (supplier activity).
  if (direction === 'sent' && createdAt) {
    const createdMs = Date.parse(createdAt);
    if (Number.isFinite(createdMs) && activityMs <= createdMs) return false;
  }

  const seenAt = state[collaborationSeenThreadKey(direction, threadId)];
  const seenMs = seenAt ? Date.parse(seenAt) : Number.NaN;
  return !Number.isFinite(seenMs) || activityMs > seenMs;
};

export const buildCollaborationThreadUrl = ({
  tenantId,
  direction,
  threadId
}: {
  tenantId: string;
  direction: CollaborationWorkDirection;
  threadId: string;
}): string => {
  const params = new URLSearchParams({
    tab: direction === 'sent' ? 'outbound' : 'inbound',
    tenantId
  });
  params.set(direction === 'sent' ? 'shareInvitationId' : 'inboxItemId', threadId);
  return `/collaboration?${params.toString()}`;
};

export const countPayloadEvidence = (payload: Record<string, unknown>): number =>
  Array.isArray(payload.evidenceManifest) ? payload.evidenceManifest.length : 0;

export const summariseCollaborationEvidence = (
  artifacts: CollaborationThreadArtifact[]
): CollaborationEvidenceSummary => {
  const count = artifacts.length;
  const quarantinedCount = artifacts.filter((artifact) => artifact.quarantineStatus === 'quarantined').length;
  const releasedCount = artifacts.filter((artifact) => artifact.quarantineStatus === 'released').length;
  const technicalFailureCount = artifacts.filter(
    (artifact) => artifact.quarantineStatus === 'rejected_technical_failure'
  ).length;

  if (technicalFailureCount > 0) {
    return {
      count,
      quarantinedCount,
      releasedCount,
      technicalFailureCount,
      label: `${count} evidence item${count === 1 ? '' : 's'} · ${technicalFailureCount} failed security checks`,
      tone: 'warning'
    };
  }
  if (quarantinedCount > 0) {
    return {
      count,
      quarantinedCount,
      releasedCount,
      technicalFailureCount,
      label: `${count} evidence item${count === 1 ? '' : 's'} · ${quarantinedCount} awaiting release`,
      tone: 'warning'
    };
  }
  if (count > 0) {
    return {
      count,
      quarantinedCount,
      releasedCount,
      technicalFailureCount,
      label: `${count} evidence item${count === 1 ? '' : 's'} available`,
      tone: 'success'
    };
  }
  return {
    count: 0,
    quarantinedCount: 0,
    releasedCount: 0,
    technicalFailureCount: 0,
    label: 'No evidence attached',
    tone: 'neutral'
  };
};

export const sentWorkActivityAt = (share: CollaborationShareInvitation): string =>
  share.selfAssessmentSubmittedAtUtc || share.selfAssessmentFirstOpenedAtUtc || share.updatedAt;

export const receivedWorkActivityAt = (item: CollaborationInboxItem): string => item.updatedAt;
